/**
 * Hero particle field (canvas). Respects prefers-reduced-motion.
 */
export function initHeroCanvas(canvas) {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const ctx = canvas.getContext("2d", { alpha: true });
  if (!ctx) return;

  let width = 0;
  let height = 0;
  let dpr = 1;
  let particles = [];
  let raf = 0;
  let tick = 0;
  const pointer = { x: 0.5, y: 0.4, active: false };

  const count = () => Math.min(150, Math.floor((width * height) / 14000));

  function resize() {
    const rect = canvas.getBoundingClientRect();
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    width = rect.width;
    height = rect.height;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (particles.length === 0) spawn();
  }

  function makeParticle() {
    const maxLife = 120 + Math.random() * 160;
    return {
      x: Math.random() * width,
      y: Math.random() * height,
      vx: 0,
      vy: 0,
      life: Math.random() * maxLife,
      maxLife,
      hue: 190 + Math.random() * 40,
    };
  }

  function spawn() {
    particles = Array.from({ length: count() }, makeParticle);
  }

  function field(x, y) {
    const nx = x / width;
    const ny = y / height;
    return (
      Math.sin(nx * 3 + tick * 4e-4) * 1.4 +
      Math.cos(ny * 3.5 - tick * 3e-4) * 1.4 +
      Math.sin((nx + ny) * 2 + tick * 2e-4) * 0.8
    );
  }

  function frame() {
    tick += 16;
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = "rgba(5, 7, 14, 0.14)";
    ctx.fillRect(0, 0, width, height);
    ctx.globalCompositeOperation = "lighter";

    for (const p of particles) {
      const angle = field(p.x, p.y);
      const damp = 0.6;
      p.vx += Math.cos(angle) * 0.12;
      p.vy += Math.sin(angle) * 0.12;

      if (pointer.active) {
        const dx = pointer.x * width - p.x;
        const dy = pointer.y * height - p.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < 40000) {
          p.vx += (dx / Math.sqrt(d2 + 1)) * 0.06;
          p.vy += (dy / Math.sqrt(d2 + 1)) * 0.06;
        }
      }

      p.vx *= 0.92;
      p.vy *= 0.92;
      p.x += p.vx * damp * 2.2;
      p.y += p.vy * damp * 2.2;
      p.life += 1;

      const lifeT = 1 - Math.abs(p.life / p.maxLife - 0.5) * 2;
      const alpha = Math.max(0, lifeT) * 0.5;
      ctx.beginPath();
      ctx.fillStyle = `hsla(${p.hue}, 90%, 65%, ${alpha})`;
      ctx.arc(p.x, p.y, 1.4, 0, Math.PI * 2);
      ctx.fill();

      if (p.life >= p.maxLife || p.x < -20 || p.x > width + 20 || p.y < -20 || p.y > height + 20) {
        Object.assign(p, makeParticle(), { life: 0 });
      }
    }

    raf = requestAnimationFrame(frame);
  }

  function staticField() {
    ctx.clearRect(0, 0, width, height);
    for (let i = 0; i < count(); i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      ctx.beginPath();
      ctx.fillStyle = `hsla(200, 90%, 65%, ${0.12 + Math.random() * 0.15})`;
      ctx.arc(x, y, 1.2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  resize();
  window.addEventListener("resize", resize, { passive: true });
  window.addEventListener(
    "pointermove",
    (e) => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = (e.clientX - rect.left) / rect.width;
      pointer.y = (e.clientY - rect.top) / rect.height;
      pointer.active = e.clientY - rect.top < rect.height;
    },
    { passive: true },
  );

  if (reduced) {
    staticField();
    return;
  }

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) cancelAnimationFrame(raf);
    else raf = requestAnimationFrame(frame);
  });
  raf = requestAnimationFrame(frame);
}
